import { useState } from "react";
import { MoreHorizontal } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteMutation } from "@/hooks/use-delete-mutation";
import { useToast } from "@/hooks/use-toast";
import { DisplayError } from "@/utils";

export type EntryRowActionsEntry = {
  id: string;
  /** The entry's description, as the clinic named it. */
  name: string;
  /** Already formatted as money and as a date, so the copy reads as one line. */
  total: string;
  maturity: string;
  isPaid: boolean;
};

type EntryRowActionsProps = {
  entry: EntryRowActionsEntry;
  onEdit: (id: string) => void;
  onWriteOff: (entry: EntryRowActionsEntry) => void;
  /** The day the entry would be marked as paid on; injected so tests are stable. */
  today?: string;
};

type OpenDialog = "write-off" | "delete" | null;

function formatToday(today?: string): string {
  if (today) return today;
  return new Date().toLocaleDateString("pt-BR");
}

/**
 * One menu per row. Paying and deleting are writes the clinic cannot take back
 * from the table, so both name the entry and its amount before they run.
 */
export function EntryRowActions({
  entry,
  onEdit,
  onWriteOff,
  today,
}: EntryRowActionsProps) {
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const { toast } = useToast();
  const { mutateAsync: deleteEntry, isLoading: isDeleting } = useDeleteMutation(
    `/finance-entries/${entry.id}`,
    "finance-entries"
  );

  const amountAndMaturity = `${entry.total}, vencimento ${entry.maturity}.`;

  async function handleDelete() {
    try {
      await deleteEntry({ endpoint: `/finance-entries/${entry.id}` });
      toast({
        title: "Lançamento excluído",
        description: `${entry.name} saiu da conta.`,
      });
    } catch (error) {
      toast({
        title: "Não foi possível excluir",
        description: DisplayError(error),
        variant: "destructive",
      });
    } finally {
      setDialog(null);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Ações do lançamento"
            className="size-10 justify-center text-app-fg-muted hover:text-app-fg-strong"
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={() => onEdit(entry.id)}>
            Editar
          </DropdownMenuItem>
          {!entry.isPaid && (
            <DropdownMenuItem onSelect={() => setDialog("write-off")}>
              Dar baixa
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-app-danger focus:text-app-danger"
            onSelect={() => setDialog("delete")}
          >
            Excluir
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={dialog === "write-off"}
        onOpenChange={(open) => setDialog(open ? "write-off" : null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dar baixa em {entry.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {amountAndMaturity} Depois disso o lançamento aparece como pago em{" "}
              {formatToday(today)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-petrol text-white hover:bg-petrol-soft"
              onClick={() => {
                setDialog(null);
                onWriteOff(entry);
              }}
            >
              Confirmar baixa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={dialog === "delete"}
        onOpenChange={(open) => setDialog(open ? "delete" : null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {entry.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {amountAndMaturity} A exclusão não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-app-danger text-white hover:bg-app-danger/90"
              isLoading={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              Excluir lançamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
